/**
 * ONE time stamp for a chat message, so two surfaces showing the same thread cannot disagree
 * about when something was said.
 *
 * ⚑ WHY THIS IS EXTRACTED RATHER THAN COPIED. F-66 puts the thread history on the teacher page's
 * Messages tab as well as in the chat panel. The panel already had this exact helper inline, and a
 * second copy on a second surface is the shape that drifts: one shows "14:05" and the other
 * "4 Nov, 14:05" for the same message, and an administrator comparing them cannot tell whether
 * she is looking at one message or two. The same reasoning as `peopleName`'s one disambiguation
 * format and `subjectLabel`'s one map.
 *
 * ⚑ TIME, AND THE DATE WHEN IT IS NOT TODAY'S. A bare "14:05" on a message sent last week reads
 * as sent today, which is worse than no stamp at all on a surface whose whole job is a
 * conversation's order. `Intl` carries both shapes and both languages, so this needs no copy.
 */

/** The reader's locale for date formatting, derived the one way the app derives it. */
export function chatLocale(i18n) {
  return (i18n?.language || "").startsWith("he") ? "he-IL" : "en-US";
}

/**
 * @param iso   the message's `created_at`, as the server returned it.
 * @param i18n  the i18next instance. Without it this still formats, in English — a stamp is a
 *              number and a month name, so the safe direction is to show it rather than hide it.
 * @param now   injectable for tests, so "is it today" is not a fact about the machine's clock.
 */
export function messageStamp(iso, { i18n, now } = {}) {
  if (!iso) return "";
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return "";
  const today = now ? new Date(now) : new Date();
  const sameDay = when.toDateString() === today.toDateString();
  return new Intl.DateTimeFormat(
    chatLocale(i18n),
    sameDay
      ? { hour: "numeric", minute: "2-digit" }
      : { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }
  ).format(when);
}

export default messageStamp;
